import React, { useState } from "react";
import useFinancialData from "./useFinancialData";
import DashboardFinancialTable from "./DashboardFinancialTable";
import WrapErrorLoading, { Wrapper } from "../../shared/components/WrapErrorLoading";
import { gettext } from "../../shared/utils/legacy";
import DashboardFinancialBar from "./DashboardFinancialBar";
import { ButtonGroup, Button } from "react-bootstrap";
import cx from "classnames";
import cropr from "../../shared/utils/cropr";
import DashboardFinancialAgGrid from "./DashboardFinancialAgGrid";
import DashboardFinancialTableYears from "./DashboardFinancialTableYears";
import { dividePerHa } from "./utils";

function toName(page: { name: string }): string {
  if (!page) return "";
  const m = /^\d+_(.*)/.exec(page.name);
  return (m && m[1]) || page.name;
}

export type BalanceAggregateType = 0 | 1 | 2; // crop, field, variety

interface DashboardFinancialProps {
  pageNr?: number;
  aggregate?: BalanceAggregateType;
  variant?: "financial" | "financialYears";
}

const Outer = ({ table, children }: { table: React.ReactNode; children: React.ReactNode }) => (
  <div className="container-fluid clearfix">
    <div className="row">
      <div className="col-12 bg-black--10 p-0">{children}</div>
    </div>
    <div className="row">
      <div className="col-12 bg-black--10 px-2">{table}</div>
    </div>
  </div>
);

function DashboardFinancial({ pageNr = 0, aggregate = 0, variant }: DashboardFinancialProps) {
  const { data, error } = useFinancialData({ aggregate });
  const [perHa, setPerHa] = useState(false);
  return (
    <WrapErrorLoading error={error} full={true}>
      {() => {
        if (!data?.pages) return;
        if (!data.pages?.length) return <Wrapper full={true}>{gettext("No data")}</Wrapper>;
        let page = data.pages[pageNr] || data.pages[0];
        if (!page) return <Wrapper full={true}>{gettext("Page not found")}</Wrapper>;
        const isSummary = !pageNr && toName(page) === "summary";
        const years = variant === "financialYears";
        if (isSummary && perHa) page = dividePerHa(page);
        const Component = years
          ? DashboardFinancialTableYears
          : pageNr
          ? DashboardFinancialTable
          : DashboardFinancialAgGrid;
        return (
          <Outer table={<Component page={page} aggregate={aggregate} perHa={perHa} />}>
            <DashboardFinancialBar isSummary={isSummary} aggregate={aggregate} variant={variant}>
              {isSummary && (
                <ButtonGroup>
                  <Button
                    variant={cx("outline-smoke", { active: !perHa })}
                    onClick={() => setPerHa(false)}
                  >
                    {gettext("Total")}
                  </Button>
                  <Button
                    variant={cx("outline-smoke", { active: perHa })}
                    onClick={() => setPerHa(true)}
                  >
                    {gettext("Per")} {cropr.l10n.unit_area_display}
                  </Button>
                </ButtonGroup>
              )}
              {!isSummary && !years && page.cropfields && (
                <Button
                  variant="outline-smoke float-right mr-2"
                  onClick={() =>
                    cropr.router.navigate(
                      `#new_record/${encodeURIComponent(
                        JSON.stringify({ cropfields: page.cropfields, theme: null })
                      )}`,
                      {
                        trigger: true,
                      }
                    )
                  }
                >
                  +{gettext("Record")}
                </Button>
              )}
            </DashboardFinancialBar>
          </Outer>
        );
      }}
    </WrapErrorLoading>
  );
}

export default DashboardFinancial;
